import { Link, useParams } from 'react-router-dom';
import { useGetInvoiceQuery, useListCustomersQuery } from '../store/apiSlice.js';
import { Loader, ErrorAlert } from '../components/States.jsx';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { SHOP, isIntraState } from '../config/shop.js';
import { amountInWords } from '../utils/amountInWords.js';
import { fmtDate, money } from '../utils/format.js';

/**
 * A GST invoice, sized for A4 and styled to print.
 *
 * Everything on it is derived from the invoice the server stored — prices, GST
 * and totals were resolved server-side when the sale was saved, so this page
 * never recomputes money. It only splits the tax the customer's state decides.
 */
export default function InvoicePage() {
  const { id } = useParams();
  const { data: invoice, isLoading, error, refetch } = useGetInvoiceQuery(id);
  const { data: customers } = useListCustomersQuery({});

  if (isLoading) {
    return (
      <>
        <PageHeader eyebrow="Invoice" title="Tax Invoice" />
        <Card><Loader label="Loading invoice…" /></Card>
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader eyebrow="Invoice" title="Tax Invoice" />
        <Card><ErrorAlert message={error.message} onRetry={refetch} /></Card>
      </>
    );
  }

  const customer = (customers || []).find((c) => c.company === invoice.customer) || {};
  const intraState = isIntraState(customer.gst);

  const taxable = invoice.lines.reduce((sum, line) => sum + (Number(line.taxableValue) || 0), 0);
  const tax = invoice.totalGst;
  const half = money(tax / 2);

  return (
    <>
      {/* The chrome. `no-print` keeps it off the paper. */}
      <div className="no-print">
        <PageHeader
          eyebrow="Invoice"
          title={invoice.invoiceNo}
          subtitle={`${invoice.customer} · ${fmtDate(invoice.date)}`}
        />
        <div className="btn-row" style={{ marginBottom: 18 }}>
          <button type="button" className="btn primary" onClick={() => window.print()}>
            Print / Save as PDF
          </button>
          <Link className="btn" to="/stock-out" style={{ textDecoration: 'none' }}>
            Back to invoices
          </Link>
          {invoice.updatedAt && (
            <span className="muted" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              Corrected on {fmtDate(invoice.updatedAt)}
            </span>
          )}
        </div>
      </div>

      <article className="invoice-sheet">
        <header className="invoice-head">
          <div>
            <div className="invoice-shop">{SHOP.name}</div>
            <div className="invoice-tagline">{SHOP.tagline}</div>
            <div className="invoice-lines">
              {SHOP.addressLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
              <div>GSTIN: {SHOP.gstin}</div>
              <div>
                {SHOP.phone} · {SHOP.email}
              </div>
            </div>
          </div>
          <div className="invoice-title-block">
            <div className="invoice-title">Tax Invoice</div>
            <table className="invoice-meta">
              <tbody>
                <tr>
                  <th>Invoice No</th>
                  <td>{invoice.invoiceNo}</td>
                </tr>
                <tr>
                  <th>Date</th>
                  <td>{fmtDate(invoice.date)}</td>
                </tr>
                <tr>
                  <th>Place of Supply</th>
                  <td>{intraState ? 'Intra-state' : 'Inter-state'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </header>

        <section className="invoice-party">
          <div>
            <div className="invoice-party-label">Billed To</div>
            <div className="invoice-party-name">{invoice.customer}</div>
            <div className="invoice-lines">
              {customer.address && <div>{customer.address}</div>}
              {customer.phone && <div>{customer.phone}</div>}
              <div>GSTIN: {customer.gst || 'Unregistered'}</div>
            </div>
          </div>
          <div>
            <div className="invoice-party-label">Customer Type</div>
            <div className="invoice-party-name">{invoice.customerType || 'Standard'}</div>
          </div>
        </section>

        <table className="invoice-table">
          <thead>
            <tr>
              <th className="sr-col">#</th>
              <th>Description</th>
              <th className="num">Qty</th>
              <th className="num">Rate (excl. GST)</th>
              <th className="num">Taxable</th>
              <th className="num">GST %</th>
              <th className="num">GST</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line, index) => (
              <tr key={`${line.product}-${line.texture}-${line.color}-${line.length}-${index}`}>
                <td className="sr-col">{index + 1}</td>
                <td>
                  <strong>{line.product}</strong>
                  <div className="invoice-sku">
                    {line.texture} · {line.length} · {line.color}
                  </div>
                </td>
                <td className="num">{line.qty}</td>
                {/* Price is stored GST inclusive; a tax invoice shows the pre-tax unit rate. */}
                <td className="num">₹{money((Number(line.taxableValue) || 0) / (line.qty || 1))}</td>
                <td className="num">₹{money(line.taxableValue)}</td>
                <td className="num">{Number(line.gst) || 0}%</td>
                <td className="num">₹{money(line.gstAmount)}</td>
                <td className="num">₹{money(line.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <section className="invoice-foot">
          <div className="invoice-words">
            <div className="invoice-party-label">Amount in Words</div>
            <div className="invoice-words-text">{amountInWords(invoice.grandTotal)}</div>

            <div className="invoice-bank">
              <div className="invoice-party-label">Bank Details</div>
              <div className="invoice-lines">
                <div>{SHOP.bank.name}</div>
                <div>A/C {SHOP.bank.account}</div>
                <div>IFSC {SHOP.bank.ifsc}</div>
              </div>
            </div>
          </div>

          <table className="invoice-totals">
            <tbody>
              <tr>
                <th>Taxable Value</th>
                <td>₹{money(taxable)}</td>
              </tr>
              {/* Same state: the tax splits in half. Another state: one IGST line. */}
              {intraState ? (
                <>
                  <tr>
                    <th>CGST</th>
                    <td>₹{half}</td>
                  </tr>
                  <tr>
                    <th>SGST</th>
                    <td>₹{money(tax - half)}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <th>IGST</th>
                  <td>₹{money(tax)}</td>
                </tr>
              )}
              <tr className="invoice-grand">
                <th>Grand Total</th>
                <td>₹{money(invoice.grandTotal)}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <footer className="invoice-terms">
          <div>
            <div className="invoice-party-label">Terms</div>
            <ol>
              {SHOP.terms.map((term) => (
                <li key={term}>{term}</li>
              ))}
            </ol>
          </div>
          <div className="invoice-sign">
            <div>For {SHOP.name}</div>
            <div className="invoice-sign-line" />
            <div>Authorised Signatory</div>
          </div>
        </footer>
      </article>
    </>
  );
}
